import {
  CognitoIdentityProviderClient,
  UpdateUserAttributesCommand,
  VerifyUserAttributeCommand,
  type CodeDeliveryDetailsType,
} from '@aws-sdk/client-cognito-identity-provider';
import { type User } from 'oidc-client-ts';
import { getCognitoRegion } from '@/shared/auth/cognitoClient';
import type { AuthTokenContent } from '@/shared/stores/authStore';

export interface UpdateUserEmailResult {
  codeDeliveryDetailsList: CodeDeliveryDetailsType[];
}

let cognitoSdkClient: CognitoIdentityProviderClient | null = null;
function getCognitoSdkClient(): CognitoIdentityProviderClient {
  if (!cognitoSdkClient) {
    cognitoSdkClient = new CognitoIdentityProviderClient({
      region: getCognitoRegion(),
    });
  }
  return cognitoSdkClient;
}

/**
 * Starts email change: Cognito sends verification code to the new address when pool requires verification.
 */
export async function updateUserEmail(
  accessToken: string,
  newEmail: string,
): Promise<UpdateUserEmailResult> {
  const client = getCognitoSdkClient();
  const result = await client.send(
    new UpdateUserAttributesCommand({
      AccessToken: accessToken,
      UserAttributes: [{ Name: 'email', Value: newEmail.trim() }],
    }),
  );
  return {
    codeDeliveryDetailsList: result.CodeDeliveryDetailsList ?? [],
  };
}

/**
 * Maps OIDC user fields into AuthTokenContent (to store in authStore).
 */
export function unpackOidcUser(user: User): AuthTokenContent {
  const profile = user.profile as Record<string, unknown>;

  const email = (typeof profile.email === 'string' && profile.email) || '';
  const firstName = (typeof profile.given_name === 'string' && profile.given_name) || '';
  const lastName = (typeof profile.family_name === 'string' && profile.family_name) || '';

  return {
    token: user.access_token || '',
    exp: user.expires_at ?? 0,
    email,
    firstName,
    lastName,
  };
}

/**
 * Completes email verification with code from the new inbox.
 */
export async function verifyUserEmail(accessToken: string, code: string): Promise<void> {
  const client = getCognitoSdkClient();
  await client.send(
    new VerifyUserAttributeCommand({
      AccessToken: accessToken,
      AttributeName: 'email',
      Code: code.trim(),
    }),
  );
}

/**
 * Maps Cognito SDK / service errors to a safe user-visible message.
 * Currently centralised around email updates operations, but could be extended.
 **/
export function mapCognitoErrorToMessage(error: unknown): string {
  const name =
    error &&
    typeof error === 'object' &&
    'name' in error &&
    typeof (error as { name: unknown }).name === 'string'
      ? (error as { name: string }).name
      : '';

  switch (name) {
    case 'AliasExistsException':
      return 'This email address is already in use.';
    case 'CodeMismatchException':
      return 'The verification code is incorrect.';
    case 'ExpiredCodeException':
      return 'The verification code has expired. Request a new code.';
    case 'TooManyRequestsException':
      return 'Too many attempts. Please wait and try again.';
    case 'NotAuthorizedException':
      return 'Your session cannot update email. Sign in again.';
    case 'CodeDeliveryFailureException':
      return 'Could not send the verification email. Check the address and try again.';
    case 'InvalidParameterException':
      return 'The email address is not valid.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
